import {Card, Chips, Slider} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import type {ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, valueOrPlaceholder, type Nullish, type TestIdProps} from 'shared/web';

import type {DownPaymentAdjustment} from 'features/financial-products';

import type {FinancialProductCalculatorResult} from '../../types/FinancialProductCalculatorResult';
import type {FinancialProductCalculatorSelection} from '../../types/FinancialProductCalculatorSelection';
import {getBracketSliderStep} from '../../utils/getBracketSliderStep';
import {CalculatorControlHeading} from '../CalculatorControlHeading/CalculatorControlHeading';
import {CalculatorValue} from '../CalculatorValue/CalculatorValue';

interface FinancialProductCalculatorProps extends TestIdProps {
  selection: FinancialProductCalculatorSelection;
  onSelectionChange: (selection: FinancialProductCalculatorSelection) => void;
  availableTerms: number[];
  /**
   * The slider runs from the first to the last value; callers pass the labelled grid (`ticks` of
   * `getFinancingControlLimits`), including the selected term's floor and matrix-wide maximum.
   */
  availableDownPaymentPercentages: number[];
  downPaymentBrackets?: number[];
  downPaymentAdjustment?: DownPaymentAdjustment | null;
  downPaymentStep: number;
  /** Empty hides the control; callers must pre-filter invalid combinations. */
  availableBalloonPaymentPercentages: number[];
  vehiclePrice: number;
  currency: string;
  result: FinancialProductCalculatorResult | Nullish;
  isDisabled?: boolean;
  badge?: ReactNode;
  /** Shown instead of the figures when there is no result; without it the figures show placeholders. */
  emptyResult?: ReactNode;
}

// Heading size 1 has a line height of eight design-system spacing units.
const MONTHLY_PAYMENT_HEIGHT = 8;
// The default `Badge` height, kept with or without the badge so the line never changes height.
const COORDINATE_LINE_HEIGHT = 6;
/**
 * The figures' natural height: padding 2 × 5, the coordinate line 6 (a badge), the monthly rate
 * 6 + 2 + 8, six value rows 6 × 5 + 5 × 2 and two stack gaps 2 × 4. The empty state is shorter,
 * so the box keeps one height whichever state it shows.
 */
const RESULT_BOX_MIN_HEIGHT = 80;

/**
 * Kept fully controlled so local vehicle pricing and backend-priced surfaces share one UI
 * without coupling the component to either pricing source.
 */
export function FinancialProductCalculator(props: FinancialProductCalculatorProps) {
  const formatCurrency = useFormatCurrency();

  const formatAmount = (amount: number | Nullish) =>
    valueOrPlaceholder(isNotNil(amount) ? formatCurrency(amount, props.currency) : null);

  const sortedTerms = [...props.availableTerms].sort((left, right) => left - right);
  const sortedDownPayments = [...props.availableDownPaymentPercentages].sort(
    (left, right) => left - right
  );
  const downPaymentMin = sortedDownPayments[0] ?? 0;
  const downPaymentMax = sortedDownPayments.at(-1) ?? downPaymentMin + 1;
  const sortedBalloonPayments = [...props.availableBalloonPaymentPercentages].sort(
    (left, right) => left - right
  );
  const balloonMin = sortedBalloonPayments[0] ?? 0;
  const balloonMax = sortedBalloonPayments.at(-1) ?? balloonMin + 1;
  const termMonths = props.selection.termMonths;
  const balloonValue = props.selection.balloonPaymentPercentage ?? balloonMin;
  const hasFigures = isNotNil(props.result) || isNil(props.emptyResult);

  return (
    <Grid columns={[1, 3]} spacing={6}>
      <GridItem span={[1, 2]}>
        <Card variant="inlineGrey" data-testid={suffixTestId('controls', props)}>
          <VStack spacing={6}>
            <VStack spacing={4}>
              <HStack justify="space-between" align="center">
                <CalculatorControlHeading
                  title={i18n.t('page.vehicle.financingAndLeasing.term')}
                  tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.term')}
                  testId={suffixTestId('termHint', props)}
                />
                <HStack spacing={2} align="center">
                  <Text alternative data-testid={suffixTestId('term', props)}>
                    {valueOrPlaceholder(termMonths)}
                  </Text>
                  <Text color="secondary">{i18n.t('general.labels.months')}</Text>
                </HStack>
              </HStack>
              <Chips
                options={sortedTerms.map((term) => ({
                  value: term.toString(),
                  label: term.toString(),
                }))}
                value={isNil(termMonths) ? [] : [termMonths.toString()]}
                isDisabled={props.isDisabled}
                onChange={(values) => {
                  const nextTermMonths = Number(values?.[0]);
                  // Re-clicking the selected chip must not re-emit the term: callers treat any
                  // emitted term as a change (e.g. they reset the adjustment hint).
                  if (!sortedTerms.includes(nextTermMonths) || nextTermMonths === termMonths) {
                    return;
                  }
                  props.onSelectionChange({...props.selection, termMonths: nextTermMonths});
                }}
                data-testid={suffixTestId('terms', props)}
              />
            </VStack>

            <VStack spacing={4}>
              <HStack justify="space-between" align="center">
                <CalculatorControlHeading
                  title={i18n.t('page.vehicle.financingAndLeasing.downPayment')}
                  tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.downPayment')}
                  testId={suffixTestId('downPaymentHint', props)}
                />
                <HStack spacing={2} align="center">
                  <Text alternative data-testid={suffixTestId('downPaymentPercentage', props)}>
                    {`${props.selection.downPaymentPercentage} %`}
                  </Text>
                  <Text color="secondary">
                    {`= ${formatAmount(
                      props.result?.downPaymentAmount ??
                        props.vehiclePrice * (props.selection.downPaymentPercentage / 100)
                    )}`}
                  </Text>
                </HStack>
              </HStack>
              <Show when={sortedDownPayments.length > 0}>
                <Slider
                  min={downPaymentMin}
                  max={downPaymentMax === downPaymentMin ? downPaymentMin + 1 : downPaymentMax}
                  step={props.downPaymentStep}
                  ticks={sortedDownPayments.map((value) => ({
                    value,
                    label: `${value} %`,
                    small:
                      isNotNil(props.downPaymentBrackets) &&
                      !props.downPaymentBrackets.includes(value),
                  }))}
                  value={props.selection.downPaymentPercentage}
                  isDisabled={props.isDisabled === true || sortedDownPayments.length < 2}
                  onChange={(downPaymentPercentage) =>
                    props.onSelectionChange({...props.selection, downPaymentPercentage})
                  }
                  data-testid={suffixTestId('downPayment', props)}
                />
              </Show>
              <Show when={isNotNil(props.downPaymentAdjustment)}>
                <span aria-live="polite" data-testid={suffixTestId('downPaymentAdjustment', props)}>
                  <Text size="small" color="warning">
                    {props.downPaymentAdjustment?.reason === 'FLOOR'
                      ? i18n.t('page.vehicle.financingAndLeasing.downPaymentRaisedToFloor', {
                          from: props.downPaymentAdjustment.fromPercentage,
                          to: props.downPaymentAdjustment.toPercentage,
                          term: props.selection.termMonths,
                        })
                      : i18n.t('page.vehicle.financingAndLeasing.downPaymentLoweredForBalloon', {
                          from: props.downPaymentAdjustment?.fromPercentage,
                          to: props.downPaymentAdjustment?.toPercentage,
                        })}
                  </Text>
                </span>
              </Show>
            </VStack>

            <Show when={sortedBalloonPayments.length > 0}>
              <VStack spacing={4}>
                <HStack justify="space-between" align="center">
                  <CalculatorControlHeading
                    title={i18n.t('page.vehicle.financingAndLeasing.balloonPayment')}
                    tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.balloonPayment')}
                    testId={suffixTestId('balloonPaymentHint', props)}
                  />
                  <HStack spacing={2} align="center">
                    <Text alternative data-testid={suffixTestId('balloonPaymentPercentage', props)}>
                      {`${balloonValue} %`}
                    </Text>
                    <Text color="secondary">
                      {`= ${formatAmount(
                        props.result?.balloonPaymentAmount ??
                          props.vehiclePrice * (balloonValue / 100)
                      )}`}
                    </Text>
                  </HStack>
                </HStack>
                <Slider
                  min={balloonMin}
                  max={balloonMax === balloonMin ? balloonMin + 1 : balloonMax}
                  step={getBracketSliderStep(sortedBalloonPayments)}
                  ticks={sortedBalloonPayments.map((value) => ({
                    value,
                    label: `${value} %`,
                  }))}
                  value={balloonValue}
                  isDisabled={props.isDisabled === true || sortedBalloonPayments.length < 2}
                  onChange={(balloonPaymentPercentage) =>
                    props.onSelectionChange({...props.selection, balloonPaymentPercentage})
                  }
                  data-testid={suffixTestId('balloonPayment', props)}
                />
              </VStack>
            </Show>
          </VStack>
        </Card>
      </GridItem>

      <GridItem>
        <Box
          backgroundColor="palettes.neutral.900.100"
          borderRadius="medium"
          boxShadow="elevation_3"
          minHeight={RESULT_BOX_MIN_HEIGHT}
          padding={5}
        >
          <VStack spacing={4}>
            <HStack justify="space-between" align="center" height={COORDINATE_LINE_HEIGHT}>
              <Text size="small" color="tertiary" alternative>
                {`${valueOrPlaceholder(termMonths)}M × ${props.selection.downPaymentPercentage} %`}
              </Text>
              {/* A shrunk badge wraps its text past its fixed height; the coordinates wrap instead. */}
              <Box flexShrink={0}>{props.badge}</Box>
            </HStack>

            <Show when={hasFigures}>
              <VStack spacing={2}>
                <Text color="tertiary">
                  {i18n.t('page.vehicle.financingAndLeasing.monthlyRateGross')}
                </Text>
                <HStack justify="flex-end" align="center" height={MONTHLY_PAYMENT_HEIGHT}>
                  <Heading
                    size={1}
                    color="warning"
                    align="right"
                    data-testid={suffixTestId('monthlyPayment', props)}
                  >
                    {formatAmount(props.result?.monthlyPayment)}
                  </Heading>
                </HStack>
              </VStack>
              <VStack spacing={2}>
                <CalculatorValue
                  label={i18n.t('page.vehicle.financingAndLeasing.nominalRate')}
                  value={`${valueOrPlaceholder(props.result?.nominalRate)} %`}
                />
                <CalculatorValue
                  label={i18n.t('page.vehicle.financingAndLeasing.apr')}
                  value={`${valueOrPlaceholder(props.result?.apr)} %`}
                />
                <CalculatorValue
                  label={i18n.t('page.vehicle.financingAndLeasing.netCredit')}
                  value={formatAmount(props.result?.netCredit)}
                />
                <CalculatorValue
                  label={i18n.t('page.vehicle.financingAndLeasing.grossCredit')}
                  value={formatAmount(props.result?.grossCredit)}
                />
                <CalculatorValue
                  label={i18n.t('page.vehicle.financingAndLeasing.totalInterest')}
                  value={formatAmount(props.result?.totalInterest)}
                />
                <CalculatorValue
                  label={i18n.t('page.vehicle.financingAndLeasing.totalAmount')}
                  value={formatAmount(props.result?.totalPayable)}
                />
              </VStack>
            </Show>

            <Show when={!hasFigures}>{props.emptyResult}</Show>
          </VStack>
        </Box>
      </GridItem>
    </Grid>
  );
}
